import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';

/** One row of a {@link ChatContextMenuComponent} — an icon, a label, and an id the caller reads back. */
export interface ChatContextMenuItem {
  id: string;
  label: string;
  icon: string;
  /** Styled red — reserve for something that changes what someone else can do, not routine actions. */
  danger?: boolean;
}

/**
 * A small right-click menu, positioned at the pointer.
 *
 * The chat module has no PrimeNG components in it — everything hand-rolled, `pi-*` icon classes
 * only — so this follows suit rather than pulling in PrimeNG's ContextMenu for what is a handful
 * of rows. First user: the sidebar's Master-Admin-only "Hide from chat" action, but it takes a
 * plain `items` list so anything else that needs a right-click menu can reuse it.
 *
 * Closes itself on an outside click, Escape, or the page scrolling/resizing out from under it —
 * a menu anchored to a point on screen that the pointer has moved away from is worse than no menu.
 */
@Component({
  selector: 'app-chat-context-menu',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="chat-context-menu" role="menu">
      <button
        type="button"
        class="chat-context-menu__item"
        [class.is-danger]="item.danger"
        *ngFor="let item of items"
        role="menuitem"
        (click)="choose(item)">
        <i class="pi" [ngClass]="item.icon"></i>
        <span>{{ item.label }}</span>
      </button>
    </div>
  `,
  styles: [
    `
      :host {
        position: fixed;
        z-index: 1000001;
        top: 0;
        left: 0;
      }

      .chat-context-menu {
        min-width: 180px;
        background: var(--white);
        border: 1px solid var(--lightGray);
        border-radius: 10px;
        box-shadow: 0 8px 28px rgba(26, 47, 69, 0.14);
        padding: 6px;
      }

      .chat-context-menu__item {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        border: none;
        background: transparent;
        padding: 8px;
        border-radius: 8px;
        cursor: pointer;
        text-align: left;
        font-size: 13px;
        color: var(--black);

        &:hover {
          background: var(--lightGrayBG);
        }

        i {
          font-size: 13px;
          width: 14px;
          flex: 0 0 auto;
          color: var(--searchIcon);
        }

        &.is-danger {
          color: var(--danger);

          i {
            color: var(--danger);
          }
        }
      }
    `,
  ],
})
export class ChatContextMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  /** Where the triggering click landed — usually `event.clientX` / `event.clientY`. */
  @Input() x = 0;
  @Input() y = 0;
  @Input() items: ChatContextMenuItem[] = [];

  /** Fires with the chosen item's `id`. The caller closes the menu — see {@link closed}. */
  @Output() picked = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  /**
   * Bound once so `removeEventListener` in {@link ngOnDestroy} actually matches what was added —
   * an inline arrow passed straight to `addEventListener` would not.
   */
  private readonly onScroll = () => this.closed.emit();

  ngOnInit(): void {
    this.host.nativeElement.style.top = `${this.y}px`;
    this.host.nativeElement.style.left = `${this.x}px`;
    // Capture phase, not `window:scroll`: the sidebar list scrolls inside its own `overflow-y`
    // container, and a scroll event on that never bubbles to the window at all. Listening on the
    // capture phase at the document root still sees it, because capturing runs before bubbling
    // would have been skipped.
    document.addEventListener('scroll', this.onScroll, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onScroll, true);
  }

  /**
   * Nudged back on screen once its real size is known — a right-click near the window's edge
   * would otherwise open a menu that runs off it.
   */
  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const overflowX = rect.right - (window.innerWidth - margin);
    const overflowY = rect.bottom - (window.innerHeight - margin);
    if (overflowX > 0) {
      el.style.left = `${Math.max(margin, this.x - overflowX)}px`;
    }
    if (overflowY > 0) {
      el.style.top = `${Math.max(margin, this.y - overflowY)}px`;
    }
  }

  choose(item: ChatContextMenuItem): void {
    this.picked.emit(item.id);
    this.closed.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.closed.emit();
    }
  }

  @HostListener('document:contextmenu', ['$event'])
  onDocumentContextMenu(event: MouseEvent): void {
    // A second right-click elsewhere should replace this menu, not stack a new one behind it —
    // closing here lets the row that was just right-clicked open its own.
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.closed.emit();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }

  @HostListener('window:resize')
  onViewportChange(): void {
    this.closed.emit();
  }
}
